import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import dayjs from 'dayjs';
import {cloneDeep, isEqual} from 'lodash';
import {
	CurbsideLocationStore,
	FeaturedItemStore,
	LocationDiscountStore,
	LocationStore,
	MenuStore,
	TicketsStore,
	PAYMENT_TYPE,
	TPaymentType,
	TrendingItemStore,
	mergeAndAdd,
	ILineItemOption,
	ILineItem,
	ILocation,
	ITicket,
	SERVICE_TYPE,
	TServiceType,
	IPaymentSubmit,
	LocationHoursStore,
	ITokenizeResult,
	SessionStore,
	BRAND,
} from '../../store';
import {RelativeDayOfWeekFilter} from '../../filters';
import {
	EventsService,
	WebService,
	DependencyService,
	ItemService,
	FactoryService,
	DateTimeService,
} from '../../services';
import {DisplayUtils} from '../../helpers';
import type {Dayjs} from 'dayjs';
import {Dialog, Notify} from 'quasar';
import type {VueComponent} from '../../types';
import {ERouteName, ORDER_ROUTES} from '../../router';
import type {
	IAddLineItemParams,
	IHqItemInfo,
	IPromiseDisplayOptions,
	IValidateItemsInCartCallbackParams,
	IValidateItemsInCartParams,
	TAddItemParams,
	TTicketResponse,
	TValidateItemsInCartCallback,
} from './order.types';
import {IAddLineItemsParams} from './order.types';

export interface IPromiseDateInternal {
	timestamp: number;
	timezone: string | undefined;
	useNextAvailable: boolean;
}

export type TPromiseDate = Dayjs;


const ORDER_ITEM_LIMIT = 15;

/**
 * model for assembling a ticket in stages
 *
 * For submitting tickets to the server or for querying submitted
 * tickets, use the TicketsStore.
 */
@Module({name: 'order', namespaced: true})
class OrderModule extends VuexModule {
	// We don't want to make a request with a token that has been removed
	// from the server, thus placing a new order. Even though we always
	// grab the time on the client before the server, meaning the client
	// will expire first, make the client TTL slightly less to be safe.
	protected static IDEMPOTENCY_TOKEN_TTL = 3540; // in seconds

	/**
	 * selected location ID
	 *
	 * This is set in the init method and elsewhere.
	 */
	selectedLocationId: string | null = null;

	/**
	 * items in the order (aka, the cart)
	 *
	 * For the current order we use these arrays to keep track of the various
	 * parts of the ticket. We put this all together to send a full "ticket" to
	 * the server. The server returns a ticket with a slightly different structure.
	 * LineItems should be identical but discounts and payments will be different.
	 * When we are proposing a ticket to the server we will pluck off the relevant
	 * parts of the response to update our local representation of the ticket.
	 */
	lineItems: ILineItem[] = [];

	promiseDateInternal: IPromiseDateInternal | null = null;

	tipAmount: number | null = null;

	// This represents the working ticket's serviceTypeId. Components that want to manipulate serviceType need to use
	// isTicketSubmitted to determine whether to update this property or call updateServiceTypeId action
	serviceTypeId: TServiceType | null = null;

	paymentType: TPaymentType | null = null;
	tokenizeResult: ITokenizeResult | null = null;

	// This is the ticket that the server returns.
	workingTicket: ITicket | null = null;

	// This will be set to true while items are being added to the order. This way it's clear
	// to the user that something is happening.
	isUpdating = false;

	orderEventsUUID: string | null = null;

	/**
	 * flag to indicate that an order is in progress and no changes or concurrent orders should be allowed
	 *
	 * This flag doesn't work in a component, which is reset on every invocation.
	 */
	orderSubmissionInProgress = false;

	/**
	 * queue of items to run through "add to cart" animation
	 *
	 * TODO: initialize to empty array each session
	 */
	cartAnimationQueue: ILineItem[] = [];

	/**
	 * This is when we have a line item but want to open the ItemView
	 * page with options already pre-selected. You will add an ILineItem
	 * to this property and then navigate to the ItemView page. The
	 * ItemView page (currently only 7Brew is supported) will then use
	 * it instead of getting the line item from the menu.
	 */
	tempLineItem: ILineItem | null = null;

	addItemAwaitingLocation: TAddItemParams | null = null;

	/**
	 * This is used to make sure requests that edit the cart that come
	 * back out of order don't overwrite newer requests.
	 */
	newestRequestIndex = 0;

	internalIdempotencyToken: {token: string; time: number} | null = null;

	get orderHasAnyDiscounts() {
		return this.lineItems.some(lineItem => lineItem.discounts.length > 0);
	}

	get itemAndItemHqIdsWithoutDiscounts() {
		const cartItemIds: string[] = [];
		for(const lineItem of this.lineItems) {
			if(lineItem.discounts.length === 0) {
				// We need to check both since offer itemIds could be either
				// itemId or itemHqId
				if(lineItem.itemId) {
					cartItemIds.push(lineItem.itemId);
				}
				if(lineItem.itemHqId) {
					cartItemIds.push(lineItem.itemHqId);
				}
			}
		}
		return cartItemIds;
	}

	get showLoading() {
		return this.isUpdating;
	}

	get orderAvailableQuantity(): number {
		return ORDER_ITEM_LIMIT - this.orderTotalQuantity;
	}

	get orderSubtotal() {
		return this.workingTicket?.subtotal ?? 0;
	}

	get orderTax() {
		return this.workingTicket?.taxAmount ?? 0;
	}

	get orderTotalQuantity() {
		return ItemService.lineItemsGetTotalQuantity(this.lineItems);
	}

	/**
	 * TODO: move to ItemService
	 */
	get hasFreeChoices() {
		return (lineItem: ILineItem, optionId: string) => {
			if(lineItem.freeChoicesSets.length === 0) {
				return false;
			}
			return lineItem.freeChoicesSets.some((fcs) => {
				return fcs.includedOptions.some((includedOptionId: string) => {
					return includedOptionId === optionId;
				});
			});
		};
	}

	/**
	 * TODO: move to ItemService
	 *
	 */
	get minMaxLimitReached() {
		return (lineItem: ILineItem, optionId: string) => {
			if(!ItemService.lineItemHasMinMaxOption(lineItem, optionId)) {
				return false;
			}

			const minMaxChoiceSet = lineItem.minMaxChoicesSets.find((fcs) => {
				return fcs.includedOptions.some((includedOptionId: string) => {
					return includedOptionId === optionId;
				});
			});
			if(!minMaxChoiceSet) {
				console.error('No min max choice set found for optionId (disabled)', optionId);
				return false;
			}

			const minMaxChoiceOptions = ItemService.lineItemGetOptionsById(lineItem, minMaxChoiceSet.includedOptions);
			const count = ItemService.optionsCountSelected(minMaxChoiceOptions, minMaxChoiceSet.countIncludedChoices);

			return count >= minMaxChoiceSet.max;
		};
	}

	/**
	 * TODO: move to MenuStore
	 *
	 */
	get option() {
		return (optionId: string) => {
			return MenuStore.menu?.options[optionId];
		};
	}

	get lineItem() {
		return (lineItemId: number) => {
			return this.lineItems.find((lineItem) => {
				return lineItem.lineItemId === lineItemId;
			}) || null;
		};
	}

	/**
	 * @deprecated use ItemService.lineItemGetById directly
	 */
	get fullItemFromId() {
		return (itemId: string | null, itemHqId: string | null, options: ILineItemOption[]): ILineItem | null => {
			return ItemService.lineItemGetById({itemId, itemHqId, itemOptions: options});
		};
	}

	get location(): ILocation | null {
		return LocationStore.location(this.selectedLocationId);
	}

	get promiseDisplay() {
		return (options: IPromiseDisplayOptions = {}): string => {
			// Only show pickup details for Curbside type
			if(this.serviceTypeId !== SERVICE_TYPE.CURBSIDE) {
				return '';
			}

			const {
				dateOptions = {
					short: true,
				},
				timeOptions = {
					format: 'h:mm a',
				}
			} = options;

			if(this.promiseDateInternal?.useNextAvailable && this.location) {
				return `Selected Pickup Time: Next Avail. ~${this.location.currentWaitTime.toString()} mins`;
			}

			let promiseDate: TPromiseDate | null = null;
			try {
				promiseDate = this.promiseDate();
			} catch(e) {}

			if(promiseDate !== null) {
				// pickup has been set
				const dateString = RelativeDayOfWeekFilter(promiseDate, dateOptions);
				const timeString = promiseDate.format(timeOptions.format) ?? '';

				return `Selected Pickup Time: ${dateString} ${timeString}`;
			} else if(this.location) {
				// no pickup set, defaulting to show Next Available (wait time)
				const nextAvailable = LocationHoursStore.nextAvailableLocationDateTime();

				if(nextAvailable && !nextAvailable.isInActiveShift) {
					/*
					 * The next available pickup time is in a future shift.
					 * Display with date and time.
					 */
					const dateString = RelativeDayOfWeekFilter(nextAvailable.date, dateOptions);
					const timeString = nextAvailable.date.format(timeOptions.format) ?? '';

					return `Next Available Pickup: ${dateString} ${timeString}`;
				} else {
					return `Next Available Pickup: ~${this.location.currentWaitTime.toString()} mins`;
				}
			}

			return '';
		};
	}

	get payment(): IPaymentSubmit {
		if(this.paymentType === null) {
			throw new Error('Payment type is null');
		}
		if(this.paymentType === PAYMENT_TYPE.CREDIT) {
			if(!this.tokenizeResult) {
				throw new Error('Tokenize Result is null');
			}
			return {
				type: this.paymentType,
				accountId: this.tokenizeResult.accountId,
				signedToken: this.tokenizeResult.signedToken,
				cvv: this.tokenizeResult.cvv,
			};
		} else if(this.paymentType === PAYMENT_TYPE.TP_GIFT_CARD) {
			return {
				type: this.paymentType,
				accountId: null,
				signedToken: null,
				cvv: null,
			};
		}
		throw new Error('Invalid payment type');
	}

	/**
	 * returns a calculated promise date based on promiseDateInternal
	 *
	 * If the user chose "next available", as determined by
	 * `useNextAvailable`, a next available datetime will be calculated
	 * by LocationHoursStore each time this method is called. If one
	 * can't be found, if the next found datetime is not today, or if
	 * it isn't in the current shift, this method throws an error.
	 *
	 * If `useNextAvailable` is false, meaning the user chose a specific,
	 * future time, we return that timestamp. If that timestamp is now in
	 * the past, this method throws an error.
	 *
	 * N.B.: This method assumes a "Next Available" `timestamp` is/was set
	 * 	     with a value that includes the location's configured (minimum)
	 * 	     wait time, and that a specific time does NOT include the
	 * 	     wait time.
	 *
	 * Finally, if no `promiseDateInternal` has been set, return null.
	 */
	get promiseDate() {
		return (): TPromiseDate | null => {
			if(this.promiseDateInternal) {
				if(this.promiseDateInternal.useNextAvailable) {
					const nextAvailable = LocationHoursStore.nextAvailableLocationDateTime();
					if(!nextAvailable) {
						console.warn('nextAvailable not found for order promiseDate');
						throw new Error('No next available pickup time found');
					} else {
						// check that next available is still today
						if(nextAvailable.date.isSame(dayjs(), 'day')) {
							if(nextAvailable.isInActiveShift) {
								return nextAvailable.date;
							} else {
								console.warn('nextAvailable pickup time outside current shift', nextAvailable);
								throw new Error('No available pickup times remain in the current shift');
							}
						}

						console.warn('nextAvailable pickup time is not today', nextAvailable);
						throw new Error('No available pickup times remain for today');
					}
				} else {
					const promiseDate = dayjs.unix(this.promiseDateInternal.timestamp).tz(this.promiseDateInternal.timezone);

					// throws error if invalid
					DateTimeService.pickupTimeValidate(promiseDate, this.location?.currentWaitTime ?? 0);

					return promiseDate;
				}
			} else {
				return null;
			}
		};
	}

	// Time based so return a function so it recalcs every time
	get activeIdempotencyToken(): () => string | null {
		return () => {
			if(this.internalIdempotencyToken === null) {
				return null;
			} else {
				const now = dayjs().unix();
				return (now - this.internalIdempotencyToken.time) <= OrderModule.IDEMPOTENCY_TOKEN_TTL ? this.internalIdempotencyToken.token : null;
			}
		};
	}

	/**
	 * checks various order requirements, throwing errors if any aren't met
	 */
	get canSubmitOrder() {
		return (): true => {
			if(!this.serviceTypeId) {
				throw new Error('No service type defined');
			}

			if(this.serviceTypeId !== SERVICE_TYPE.DRIVE_THRU) {
				if(!this.promiseDateInternal) {
					throw new Error('No pickup time provided');
				}

				// allow promiseDate's errors to bubble up
				if(this.promiseDate() === null) {
					// this should be redundant to the promiseDateInternal check above
					throw new Error('No pickup time selected');
				}
			}

			return true;
		};
	}


	@Action
	public async init(appInit = false): Promise<void> {
		this.INIT();

		if(appInit) {
			// assume the user's promise time needs to be reselected
			this.clearPromiseDate();
		}

		this.saveItemAwaitingLocation(null);
		this.setIsUpdating(false);
		// Make sure we don't get stuck in a submitting state
		this.setOrderSubmissionInProgress(false);
		if(this.selectedLocationId) {
			// Get location
			const location = LocationStore.location(this.selectedLocationId);
			if(location) {
				this.selectLocation(location)
					.then(async () => {
						const activeIdempotencyToken = this.activeIdempotencyToken();
						if(activeIdempotencyToken) {
							// If we have an idempotent token it can mean one of several things:
							//  - The original request is still in flight
							//  - The original request failed to place the order
							//  - The original request succeeded to place the order
							// In the case the order was successfully placed, check canSubmitTicket.
							// This checks if we fetched any active tickets.
							// Note that the active tickets request may NOT have finished, but if
							// it has we don't need to make another.
							if(TicketsStore.canSubmitTicket) {
								this.setOrderSubmissionInProgress();
								// Don't go through OrderStore so we can just make a placeholder ticket.
								let ticketId = null;
								try {
									ticketId = await TicketsStore.submitTicket(FactoryService.ticketSubmit());
								} catch(error: unknown) {
									console.error('Failed idempotent retry', error);
								}
								this.postTicketSubmit(ticketId);
							} else {
								// We got an active ticket back
								// Do order store cleanup
								const activeTicket = TicketsStore.activeTicket;
								if(activeTicket) {
									this.postTicketSubmit(activeTicket.ticketId);
								}
							}
							// Always clear the idempotent token
							this.clearActiveIdempotencyToken();
						}
						this.revalidateItemsInCart();
					})
					.catch((error) => {
						console.error('Error initializing order:', error);
						this.clearLocationId();
					})
				;
			} else {
				console.error('Location not found: ', this.selectedLocationId);
				this.clearLocationId();
				await DependencyService.getRouter().push({name: ERouteName.Home});
			}
		}

		/**
		 * @Action wraps this method in a promise, but IDEs and TypeScript don't pick up
		 * on that. In order to use this method in App's initStores' Promise.all() list
		 * without complaint, we want to explicitly (and redundantly) return a promise.
		 * This method SHOULD NOT block initStores (with await) unless strictly necessary.
		 */
		return Promise.resolve();
	}

	@Action
	public async selectLocation(location: ILocation) {
		// Update the selected location
		this.setLocationId(location.id);
		const promises: Promise<void>[] = [];
		// Fetch menu
		promises.push(MenuStore.fetchMenu(location.liveMenuId));
		// Get Trending Items
		if(SessionStore.isBrand(BRAND.GENERIC)) {
			promises.push(TrendingItemStore.fetchTrendingItems(location.id));
		} else {
			// 7Brew uses an advanced trending item system for now
			// hopefully we can generalize this in the future
			promises.push(TrendingItemStore.fetchTrendingItemsWithOptions(location.id));
		}
		// Get Featured Items
		promises.push(FeaturedItemStore.fetchFeaturedItems(location.id));
		// Get Discounts
		promises.push(LocationDiscountStore.fetchLocationDiscounts(location.id));
		// Get Curbside Locations
		promises.push(CurbsideLocationStore.fetchLocations(location.id));
		// Get Hours
		promises.push(LocationHoursStore.fetchLocationHours(location.id));

		// Wait for everything to load
		await Promise.all(promises);
	}

	@Action
	public async proposeTicket() {
		// If we leave a gap the server will always "re-index" the line items
		// This way we close any gaps before we submit up to the server
		this.resetLineItemIds();
		this.incrementRequestIndex();
		const currentRequestIndex = this.newestRequestIndex;

		let pickupTime: number | null = null;
		try {
			const promiseTimestamp = this.promiseDate()?.unix();
			if(promiseTimestamp) {
				pickupTime = promiseTimestamp;
			}
		} catch(e) {}

		const response = await WebService.getOrderingClient().post<TTicketResponse>(
			'/tickets/submit_app',
			{
				serviceType: this.serviceTypeId,
				items: this.lineItems,
				orderNotes: '',
				payments: [{type: PAYMENT_TYPE.NONE}],
				gratuity: this.tipAmount || 0,
				pickupTime,
			}, {
				params: {
					proposed: 1,
				}
			}
		);

		if(currentRequestIndex !== this.newestRequestIndex) {
			console.log('Proposed ticket response ignored due to newer request');
			return;
		}

		this.setWorkingTicket(response.data.data.ticket);
		this.ADD_LINE_ITEM(response.data.data.ticket.lineItems);
	}

	@Action
	public async submitWorkingTicket(requiresPayment: boolean = true) {
		// allow canSubmitOrder's errors to bubble up
		if(this.canSubmitOrder()) {
			this.setOrderSubmissionInProgress();

			// We're defaulting to a date 15 minutes in the future. Date shouldn't
			// be required for a drive thru order. This is a band-aid until we can
			// update the server to not require a pickupTime for drive thru.
			// see CU-8687y2621
			let pickupTime = dayjs().add(15, 'minutes').unix();
			try {
				const promiseTimestamp = this.promiseDate()?.unix();
				if(promiseTimestamp) {
					pickupTime = promiseTimestamp;
				}
			} catch(e) {}

			let ticketId: null | string = null;
			try {
				ticketId = await TicketsStore.submitTicket({
					gratuity: this.tipAmount ?? 0,
					items: this.lineItems,
					orderNotes: '',
					payments: requiresPayment ? [this.payment] : null,
					serviceType: this.serviceTypeId as TServiceType,
					pickupTime,
					meta: {
						[EventsService.TEMP_ORDER_ID_PROPERTY_NAME]: this.orderEventsUUID
					}
				});
			} finally {
				this.postTicketSubmit(ticketId);
			}
		}
	}

	@Action
	public postTicketSubmit(ticketId: string | null) {
		// Always toggle submission in progress off
		this.setOrderSubmissionInProgress(false);

		// If we successfully placed an order do some additional cleanup
		if(ticketId !== null) {
			EventsService.recordOrderPlaced(ticketId);
			this.clearWorkingTicket();
			this.clearCurrentOrder();
			this.clearPromiseDate();
			TicketsStore.setHasShownActiveOrderWarning(false);
		}
	}

	@Action
	public async applyMasterOptionException(lineItem: ILineItem) {
		lineItem = cloneDeep(lineItem);
		// Fake lineItem to 1 so that validation plays nice, it's "0" isn't liked
		const originalLineItemId = lineItem.lineItemId;
		lineItem.lineItemId = 1;
		// Removing discounts because server side endpoint doesn't like discounts
		// without associated lineItemId's
		const discounts = cloneDeep(lineItem.discounts);
		lineItem.discounts = [];
		const response = await WebService.getOrderingClient().post<TTicketResponse>('/tickets/submit_app', {
			serviceType: SERVICE_TYPE.PICKUP,
			items: [lineItem],
			orderNotes: '',
			payments: [{
				type: PAYMENT_TYPE.NONE
			}],
			gratuity: this.tipAmount || 0
		}, {
			params: {
				proposed: 1,
				skipOptionValidation: 1
			}
		});
		const updatedLineItem = response.data.data.ticket.lineItems[0];
		// Ignore the line item id from the server, we want to keep the original
		updatedLineItem.lineItemId = originalLineItemId;
		// Reapplying the discounts
		updatedLineItem.discounts = discounts;
		return updatedLineItem;
	}

	@Action
	public async removeDiscount(lineItem: ILineItem) {
		return this.addLineItem({
			lineItem: {
				...lineItem,
				discounts: []
			},
			showToast: true
		});
	}

	@Action
	public async addLineItem(params: IAddLineItemParams) {
		// abort if we're already adding an item
		if(this.isUpdating) {
			return;
		}

		const qtyToAdd = ItemService.lineItemsGetTotalQuantity(params.lineItem);
		let partialQty;
		if(qtyToAdd > this.orderAvailableQuantity) {
			let message = `You have reached the limit of ${ORDER_ITEM_LIMIT} items per order.`;

			// attempt to add what we can
			if(qtyToAdd > 1 && this.orderAvailableQuantity > 0) {
				partialQty = this.orderAvailableQuantity;
				message = `Your item quantity has been reduced to ${partialQty} to satisfy the limit of ${ORDER_ITEM_LIMIT} items per order.`;
			}

			this.displayToastNotification(message);

			// if there's absolutely no room, abort
			if(!partialQty) {
				return;
			}
		}

		this.setIsUpdating(true);

		try {
			const lineItem = cloneDeep(params.lineItem);
			if(partialQty) {
				lineItem.quantity = partialQty;
			}
			const updated = lineItem.lineItemId !== 0;

			const {
				// showAnimation = true,
				showToast = false,
			} = params;

			// backup existing cart
			const existingLineItems = cloneDeep(this.lineItems);

			this.ADD_LINE_ITEM(lineItem);

			try {
				await this.proposeTicket();

				// only enqueue if successful
				// if(showAnimation) {
				this.enqueueCartAnimation(lineItem);
				// }
			} catch(error) {
				// Rollback Cart
				this.SET_LINE_ITEMS(existingLineItems);
				throw error;
			}

			if(showToast) {
				let message = DisplayUtils.getLineItemDisplayName(lineItem);
				message += updated ? ' updated' : ' added to order';
				this.displayToastNotification(message);
			}
		} finally {
			this.setIsUpdating(false);
		}
	}

	/**
	 * main method to add an item from any UI
	 *
	 * This method will warn the user if there's an order in progress and wait for a response before
	 * continuing.
	 *
	 * If the OrderStore's location is not set, the user will be prompted to choose one. When prompting,
	 * the given parameters are stashed so that the method can be called again after the location is set.
	 *
	 * Even if we receive a full lineItem, we still need to call ItemService.lineItemGetById. So that we
	 * don't look up the item twice, this method can take a TItemIdentifier *instead of* a
	 * `lineItem: ILineItem` for instances where we already know we don't have a full item, but don't want
	 * to look it up to pass in details with which to look it up.
	 *
	 * @param params
	 */
	@Action
	public async addLineItemWithUserPrompts(params: TAddItemParams) {
		try {
			await this.addLineItemPromptActiveOrder(params);

			/*
			 * N.B.: await-ing addLineItemPromptLocation is a little misgiving, because
			 *       its promise doesn't wait for the user to interact with its dialogs.
			 *       In other words, we'll return here and continue/complete while the
			 *       dialogComponentLocation or dialogComponentConfirmation is displayed.
			 *       To wait for those would require adding callbacks as was done for
			 *       dialogComponentActiveTicket in addLineItemPromptActiveOrder.
			 */
			await this.addLineItemPromptLocation(params);
		} catch(e: unknown) {
			/*
			 * Currently, the only error occurs when the user aborts creating an order to view the
			 * existing one. We need that as an error so that the subsequent step(s) are skipped.
			 */
			console.warn('WARNING: ', (e as Error).message);
			WebService.showError(e);
			throw e;
		}
	}

	@Action
	public async removeLineItem(lineItemId: number) {
		this.setIsUpdating(true);
		this.REMOVE_LINE_ITEM(lineItemId);
		if(this.lineItems.length !== 0) {
			try {
				await this.proposeTicket();
			} finally {
				this.setIsUpdating(false);
			}
		}
		this.setIsUpdating(false);
	}

	@Action
	public async routeToOrder() {
		// If we were asked to choose a location while adding an item, resume adding that item.
		if(this.addItemAwaitingLocation) {
			await this.addLineItemWithUserPrompts(this.addItemAwaitingLocation);
			this.saveItemAwaitingLocation(null);
		} else {
			if(!ORDER_ROUTES.includes(DependencyService.getRouter().currentRoute.name as ERouteName)) {
				await DependencyService.getRouter().push({name: ERouteName.Order});
			}
		}
	}

	/**
	 * validates items in cart with a location change and prompts to revert if cart changes
	 *
	 * This method calls validateItemsInCart with the assumption (but not requirement) that
	 * the location is changing (i.e., that newLocation is different from this.selectedLocationId).
	 * It provides the user the ability to roll back the migration (and location) if he
	 * doesn't want to accept the changes to the cart.
	 *
	 * @param params
	 */
	@Action
	public async migrateOrder(params: {newLocation: ILocation; dialogComponent: VueComponent}) {
		const previousLocation = cloneDeep(this.location);

		const validationCompleteCallback: TValidateItemsInCartCallback = async (callbackParams) => {
			const {
				itemListMigrate,
				itemListRemoved,
				itemListRollback,
			} = callbackParams;

			if(itemListRemoved.length > 0) {
				Dialog.create({
					title: 'Confirm',
					component: params.dialogComponent,
					lineItems: itemListRemoved,
				})
					.onOk(async () => {
						await this.addLineItems({
							lineItems: itemListMigrate,
						});
						this.routeToOrder();
					})
					.onCancel(async () => {
						if(previousLocation) {
							await this.selectLocation(previousLocation);
							// TODO?: Do we want to revalidate when rolling back? Tne cart is/was valid unless the first location's menu changed in the interim.
							await this.addLineItems({
								lineItems: itemListRollback,
							});
						}
						this.routeToOrder();
					})
				;
			} else {
				await this.addLineItems({
					lineItems: itemListMigrate,
				});
				this.routeToOrder();
			}
		};

		return this.validateItemsInCart({
			menuLocation: params.newLocation,
			previousLocation,
			validationCompleteCallback,
		});
	}

	/**
	 * shared validation code
	 *
	 * This method handles (re-)validating all items in the cart based on the menu of the
	 * last selected location (this.selectedLocationId).
	 *
	 * @param params
	 */
	@Action
	public async validateItemsInCart(params: IValidateItemsInCartParams) {
		const {
			menuLocation = this.location,
			previousLocation = null,
			// default callback migrates with no confirmation or notification
			validationCompleteCallback = (callbackParams) => {
				console.debug('Validation callback is migrating silently.');
				this.addLineItems({
					lineItems: callbackParams.itemListMigrate,
				});
			},
		} = params;
		// short-circuit if no location to use for validation
		if(!menuLocation) {
			console.warn('No location provided for cart validation.');
			return;
		}

		// const isLocationChange = !previousLocation || menuLocation.id !== previousLocation.id;
		const isLocationChange = previousLocation && menuLocation.id !== previousLocation.id;
		// The items at this point might be full lineItems but it's ok to call this
		const fullLineItems = ItemService.lineItemsInflateFromLocalStorage(cloneDeep(this.lineItems));

		// create copies of existing cart, then clear
		const cartBackup: IValidateItemsInCartCallbackParams['itemListRollback'] = cloneDeep(fullLineItems);
		const cartItems: ILineItem[] = cloneDeep(fullLineItems);

		this.clearLineItems();

		/*
		 * This can't be a map or lookup table (i.e., map[hqId] = lineItem). If
		 * we don't find the line item on the HQ menu, we won't have an itemHqId
		 * for it. If we were mapping by that value, only the last of any
		 * location-only lineItems (at key '') would be retained.
		 */
		const cartHqItemInfo: IHqItemInfo[] = [];

		/*
		 * At the end of this method, the combined items from itemsToCopy and
		 * itemsMissing should account for the all the items in cartBackup.
		 */
		const itemsToCopy: IValidateItemsInCartCallbackParams['itemListMigrate'] = [];
		const itemsMissing: IValidateItemsInCartCallbackParams['itemListRemoved'] = [];

		/*
		 * Build a list of item HQ IDs and their corresponding line items.
		 * Items available only at this location will have an empty itemHqId.
		 *
		 * This must be done before selecting a new location.
		 */
		cartItems.forEach(cartItem => {
			const itemHqId = MenuStore.item(cartItem.itemId)?.itemHqId ?? '';
			cartHqItemInfo.push({
				itemHqId,
				lineItem: cartItem,
			});
		});

		if(isLocationChange) {
			await this.selectLocation(menuLocation);
		}

		// Verify items are all valid. If so, update line items with data from the new location's menu.
		cartHqItemInfo.forEach(cartInfo => {
			/*
			 * fullItemFromId is an expensive check. If the location has changed,
			 * we can skip validating items specific to the previous location
			 * (itemHqId === ''), knowing there won't be a match at other locations.
			 * If the location hasn't changed, we need to validate the options on
			 * those items, as well.
			 */
			if(!cartInfo.itemHqId && isLocationChange) {
				console.debug(`Cart validation skipping location-only item ${cartInfo.lineItem.itemId}.`);
				itemsMissing.push(cartInfo.lineItem);
				return;
			}

			// verify all options are still available
			try {
				const locationLineItem = this.fullItemFromId(cartInfo.lineItem.itemId, cartInfo.itemHqId, cartInfo.lineItem.options);
				const locationMenuItem = MenuStore.item(cartInfo.lineItem.itemId, cartInfo.itemHqId);
				if(locationLineItem && !locationLineItem.reconfigure && locationMenuItem && !locationMenuItem.outOfStock) {
					const proposedItem = {
						...cartInfo.lineItem,
						itemId: locationLineItem.itemId,
						itemHqId: locationLineItem.itemHqId,
						locationId: locationLineItem.locationId,
						menuId: locationLineItem.menuId,
						options: locationLineItem.options,
					};
					if(ItemService.lineItemIsValid(proposedItem)) {
						itemsToCopy.push(proposedItem);
					} else {
						itemsMissing.push(cartInfo.lineItem);
					}
				} else {
					itemsMissing.push(cartInfo.lineItem);
				}
			} catch(error) {
				itemsMissing.push(cartInfo.lineItem);
			}
		});

		validationCompleteCallback({
			itemListMigrate: itemsToCopy,
			itemListRemoved: itemsMissing,
			itemListRollback: cartBackup,
		});
	}

	/**
	 * validates items in cart
	 *
	 * Unlike migrateOrder, which assumes a change in location, this method doesn't care
	 * about location. Further, there is no option for the user to cancel the validation
	 * and rollback; the change completes and provides only a notification of the count
	 * of changed items. This method is expected to be called from init after setting
	 * location. In other words, the location should have been updated just prior to this
	 * method's invocation.
	 */
	@Action
	public async revalidateItemsInCart() {
		const validationCompleteCallback: TValidateItemsInCartCallback = async (callbackParams) => {
			const {
				itemListMigrate,
				itemListRemoved,
			} = callbackParams;

			await this.addLineItems({
				lineItems: itemListMigrate,
			});

			const removedCount = itemListRemoved.length;

			if(removedCount > 0) {
				this.displayToastNotification(`Your cart has been updated. ${removedCount} item${removedCount > 1 ? 's are' : ' is'} no longer available and ${removedCount > 1 ? 'have' : 'has'} been removed.`);
			}
		};

		return this.validateItemsInCart({
			validationCompleteCallback,
		});
	}

	@Action
	public startOrder(params: {dialogComponent: VueComponent}) {
		Dialog.create({
			component: params.dialogComponent
		});
		// 🫡 Salute to code that could have been and may yet be some day
		// let openLocationPicker = false;
		// if(this.location) {
		// 	if(this.location.closingTime) {
		// 		await router.push({name: 'Order'});
		// 	} else {
		// 		openLocationPicker = true;
		// 		Notify.create({
		// 			position: 'bottom',
		// 			message: 'Selected location is closed. Please choose a new location.',
		// 			color: 'tp-toast',
		// 			textColor: 'white',
		// 		});
		// 	}
		// } else {
		// 	openLocationPicker = true;
		// }
		// if(openLocationPicker) {
		// 	Dialog.create({
		// 		component: LocationPicker,
		// 	});
		// }
	}

	@Action
	public clearAll() {
		this.clearCurrentOrder();
		this.clearPromiseDate();
		this.clearLineItems();
		this.clearLocationId();
		this.clearOrderEventUUID();
		this.clearPaymentType();
		this.clearTempLineItem();
		this.clearTokenizeResult();
		this.clearWorkingTicket();
		this.clearActiveIdempotencyToken();
	}


	@Mutation
	INIT() {
		if(!this.lineItems) {
			this.lineItems = [];
		}
		this.newestRequestIndex = 0;
	}

	@Mutation
	resetLineItemIds() {
		if(this.lineItems) {
			let lineItemId = 1;
			for(const lineItem of this.lineItems) {
				lineItem.lineItemId = lineItemId++;
			}
		}
	}

	@Mutation
	ADD_LINE_ITEM(lineItem: ILineItem | ILineItem[]) {
		if(!this.lineItems) {
			this.lineItems = [];
		}

		// Get Max lineItemId
		let maxLineItemId = 0;
		if(this.lineItems.length > 0) {
			maxLineItemId = Math.max(...this.lineItems.map((li) => {
				return li.lineItemId;
			}));
		}

		// If we don't have lineItemIds then we need to add them
		const lineItems = (Array.isArray(lineItem) ? lineItem : [lineItem]).map((li) => {
			if(li.lineItemId === 0) {
				li.lineItemId = ++maxLineItemId;
			}
			return li;
		});
		mergeAndAdd(this.lineItems, lineItems, ['lineItemId']);
	}

	@Mutation
	SET_LINE_ITEMS(lineItems: ILineItem[]) {
		this.lineItems = lineItems;
	}

	@Mutation
	public REMOVE_LINE_ITEM(lineItemId: number) {
		if(!this.lineItems) {
			this.lineItems = [];
		}
		const index = this.lineItems.findIndex((lineItem) => {
			return lineItem.lineItemId === lineItemId;
		});
		if(index > -1) {
			this.lineItems.splice(index, 1);
		}
	}

	@Mutation
	setServiceTypeId(type: TServiceType) {
		// We might call this setter passing in a TPickupOptions, which is a subset of TServiceType
		// However, TPickupOptions will come in as strings since they are keys of an object.
		// Make sure they are numbers when setting them back to serviceTypeId.
		this.serviceTypeId = Number(type) as TServiceType;
	}

	@Mutation
	public clearCurrentOrder() {
		this.lineItems = [];
		this.serviceTypeId = null;
		this.tipAmount = null;
		this.paymentType = null;
		this.tokenizeResult = null;
		this.internalIdempotencyToken = null;
	}

	@Mutation
	public clearLineItems() {
		this.lineItems = [];
	}

	@Mutation
	public setLocationId(locationId: string) {
		this.selectedLocationId = locationId;
	}

	@Mutation
	public clearLocationId() {
		this.selectedLocationId = null;
	}

	@Mutation
	public setOrderSubmissionInProgress(state = true) {
		this.orderSubmissionInProgress = state;
	}

	@Mutation
	public setWorkingTicket(ticket: ITicket) {
		if(!isEqual(this.workingTicket, ticket)) {
			this.workingTicket = ticket;
		}
	}

	@Mutation
	clearWorkingTicket() {
		this.workingTicket = null;
	}

	@Mutation
	public setPromiseDate(params: {promiseDate: Dayjs; useNextAvailable?: boolean}) {
		const {
			promiseDate,
			useNextAvailable = true,
		} = params;

		this.promiseDateInternal = {
			timestamp: promiseDate.unix(),
			timezone: (promiseDate as unknown as any)?.$x?.$timezone, // Is there a better way to get the dumb timezone off a dayjs object
			useNextAvailable,
		};
	}

	@Mutation
	clearPromiseDate() {
		this.promiseDateInternal = null;
	}

	@Mutation
	public setTipAmount(tipAmount: number | null) {
		this.tipAmount = tipAmount;
	}

	@Mutation
	public setPaymentType(paymentType: TPaymentType) {
		this.paymentType = paymentType;
	}

	@Mutation
	public clearPaymentType() {
		this.paymentType = null;
	}

	@Mutation
	public setTokenizeResult(tokenizeResult: ITokenizeResult) {
		this.tokenizeResult = tokenizeResult;
	}

	@Mutation
	public clearTokenizeResult() {
		this.tokenizeResult = null;
	}

	@Mutation
	public setIsUpdating(value: boolean) {
		this.isUpdating = value;
	}

	@Mutation
	public setOrderEventUUID(orderEventUUID: string) {
		this.orderEventsUUID = orderEventUUID;
	}

	@Mutation
	public clearOrderEventUUID() {
		this.orderEventsUUID = null;
	}

	@Mutation
	public clearCartAnimationQueue() {
		this.cartAnimationQueue.length = 0;
	}

	@Mutation
	public enqueueCartAnimation(item: ILineItem | ILineItem[]) {
		if(Array.isArray(item)) {
			this.cartAnimationQueue.push(...item);
		} else {
			this.cartAnimationQueue.push(item);
		}
	}

	@Mutation
	public dequeueCartAnimation(itemId: ILineItem['lineItemId']) {
		const removedItem = this.cartAnimationQueue.shift();
		/*
		 * For some reason, ViewCartButton is mounted twice. As a consequence,
		 * it adds two watches on cartAnimationQueue running simultaneously.
		 * The first will finish and dequeue as expected, but the second will
		 * dequeue from an empty queue. As a work-around, check that removedItem
		 * is a value (not undefined) before throwing an error.
		 *
		 * TODO: fix doubly-mounted ViewCartButton
		 */
		if(removedItem && removedItem.lineItemId !== itemId) {
			throw new Error('Error dequeuing item. Item mismatch.');
		}
	}

	@Mutation
	public setTempLineItem(lineItem: ILineItem) {
		this.tempLineItem = lineItem;
	}

	@Mutation
	public clearTempLineItem() {
		this.tempLineItem = null;
	}

	@Mutation
	setActiveIdempotencyToken(token: string) {
		this.internalIdempotencyToken = {
			token: token,
			time: dayjs().unix()
		};
	}

	@Mutation
	clearActiveIdempotencyToken() {
		this.internalIdempotencyToken = null;
	}

	/**
	 * warns the user if there's an order in progress and awaits a response before continuing
	 *
	 * @param params
	 * @private
	 */
	@Action
	private addLineItemPromptActiveOrder(params: TAddItemParams): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			if(!TicketsStore.canSubmitTicket) {
				// Only show the warning once per session.
				if(TicketsStore.hasShownActiveOrderWarning) {
					resolve();
					return;
				}

				/*
				 * There is at least one, non-canceled ticket in the list of active tickets.
				 * Trigger the warning dialog (which may still have its own display logic).
				 */
				Dialog.create({
					component: params.dialogComponentActiveTicket,
					onPrimaryAction: () => {
						// user is viewing existing order and aborting creation of new one
						reject(Error('Order creation aborted.'));
					},
					onSecondaryAction: () => {
						// user is ignoring warning and creating a new order
						resolve();
					}
				});
			} else {
				resolve();
			}
		});
	}

	/**
	 * prompts the user to select a location for the order
	 *
	 * @param params
	 * @private
	 */
	@Action
	private async addLineItemPromptLocation(params: TAddItemParams): Promise<void> {
		/*
		 * If the order doesn't have a location, save the parameters, then prompt the user
		 * for location using dialogComponentLocation.
		 */
		if(!this.selectedLocationId) {
			this.saveItemAwaitingLocation(params);
			Dialog.create({
				component: params.dialogComponentLocation,
			});
		} else {
			/*
			 * We have a location (possibly after prompting the user).
			 * Continue building order.
			 */
			const {
				lineItem = null,
				itemDescriptor = null,
				hookLocationSelected = null,
			} = params;
			let fullLineItem: ILineItem | null = null;

			if(lineItem) {
				fullLineItem = ItemService.lineItemGetById({
					itemId: lineItem.itemId,
					itemHqId: lineItem.itemHqId,
					itemOptions: lineItem.options,
				});
			} else if(itemDescriptor) {
				fullLineItem = ItemService.lineItemGetById({
					itemId: itemDescriptor?.itemId ?? null,
					itemHqId: itemDescriptor?.itemHqId ?? null,
					itemOptions: itemDescriptor?.itemOptions ?? [],
				});
			}

			/*
			 * We may lack both a lineItem and an itemDescriptor. In that case,
			 * our last hope is that the hook callback will provide it to us.
			 */
			if(typeof hookLocationSelected === 'function') {
				try {
					fullLineItem = await hookLocationSelected(fullLineItem);
				} catch(e) {
					console.warn('Location selected hook threw an error: ', e);
				}
			}

			if(fullLineItem) {
				// Check if item is out of stock before going through the steps to
				// check if the options are valid.
				if(fullLineItem.itemId && fullLineItem.itemHqId) {
					if(MenuStore.item(fullLineItem.itemId, fullLineItem.itemHqId)?.outOfStock) {
						this.displayToastNotification(`${fullLineItem.name} is out of stock at your selected location.`);
						return;
					}
				}
				// use lineItem's engagementSource if not set (e.g., by hookLocationSelected)
				if(lineItem?.engagementSource && fullLineItem.engagementSource === null) {
					fullLineItem.engagementSource = lineItem.engagementSource;
				}

				// Check if line item is valid before adding to card
				if(ItemService.lineItemIsValid(fullLineItem)) {
					await this.addLineItem({
						lineItem: fullLineItem,
						showToast: false
					});
				} else {
					this.setTempLineItem(fullLineItem);
					await DependencyService.getRouter().push({
						name: ERouteName.MenuItem,
						params: {
							itemId: fullLineItem.itemId
						}
					});
					return;
				}
			} else {
				// message: `${DisplayUtils.getRecentItemDisplayName(params.recentItem)} is not available at your selected location.`,
				this.displayToastNotification('Item is not available at your selected location.');
			}
			await DependencyService.getRouter().push({name: ERouteName.Order});
			Dialog.create({component: params.dialogComponentConfirmation});
		}
	}

	@Action
	private async addLineItems(params: IAddLineItemsParams) {
		// short-circuit if given an empty list (e.g., if validation removes everything from cart)
		if(params.lineItems.length === 0) {
			return;
		}

		// abort if we're already adding an item
		if(this.isUpdating) {
			return;
		}

		const qtyToAdd = ItemService.lineItemsGetTotalQuantity(params.lineItems);
		if(qtyToAdd > this.orderAvailableQuantity) {
			this.displayToastNotification(`Adding these items exceeds the limit of ${ORDER_ITEM_LIMIT} items per order.`);
			return;
		}

		this.setIsUpdating(true);

		try {
			const lineItems = cloneDeep(params.lineItems);

			// backup existing cart
			const existingLineItems = cloneDeep(this.lineItems);

			this.ADD_LINE_ITEM(lineItems);

			try {
				await this.proposeTicket();
			} catch(error) {
				// restore cart from backup
				this.SET_LINE_ITEMS(existingLineItems);
				throw error;
			}
		} finally {
			this.setIsUpdating(false);
		}
	}

	@Action
	private displayToastNotification(message: string) {
		Notify.create({
			position: 'bottom',
			message,
			color: 'tp-toast',
			textColor: 'white',
		});
	}


	@Mutation
	private saveItemAwaitingLocation(params: TAddItemParams | null) {
		this.addItemAwaitingLocation = params;
	}

	@Mutation
	private incrementRequestIndex() {
		this.newestRequestIndex++;
	}
}

export {
	OrderModule
};
